// ─────────────────────────────────────────────────────────────────────────────
// SITUATIONAL LEADERSHIP — Scoring Engine
// Input:  raw answers {questionId: "S1"|"S2"|"S3"|"S4"}
// Output: scores {S1, S2, S3, S4}, dominant, secondary, flexibility
// ─────────────────────────────────────────────────────────────────────────────
import { QUESTIONS } from "./questions.js";

export function scoreSL(answers = {}) {
  const counts = { S1: 0, S2: 0, S3: 0, S4: 0 };

  QUESTIONS.forEach(q => {
    const ans = answers[q.id] || answers[String(q.id)];
    if (ans && counts[ans] !== undefined) counts[ans]++;
  });

  const answered = Object.values(counts).reduce((a, b) => a + b, 0);

  // Scores as % of total * 10 (0–10 scale)
  const scores = {};
  Object.keys(counts).forEach(s => {
    scores[s] = answered > 0 ? +(( counts[s] / answered) * 10).toFixed(1) : 0;
  });

  // Dominant style
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const dominant   = sorted[0][0];
  const secondary  = sorted[1][0];

  // Flexibility index (0–10): lower range = more flexible
  const maxCount = sorted[0][1];
  const minCount = sorted[sorted.length - 1][1];
  const range = maxCount - minCount;
  const flexibility = answered > 0
    ? Math.max(0, Math.round(10 - (range / (answered / 4)) * 10))
    : 0;

  // Adaptability index
  const ideal = answered / 4;
  const deviation = Object.values(counts).reduce((s, c) => s + Math.abs(c - ideal), 0);
  const adaptability = answered > 0
    ? Math.max(0, Math.round(10 - (deviation / answered) * 10))
    : 0;

  // Sorted for meta fields App.js needs
  const s0 = sorted[0][0];
  const s1 = sorted[1][0];
  const s2 = sorted[2]?.[0] || null;
  const s3 = sorted[3]?.[0] || null;

  return {
    // Core SL fields
    scores,       // { S1: 2.5, S2: 4.5, S3: 1.5, S4: 1.5 }
    counts,       // { S1: 4, S2: 7, S3: 2, S4: 3 }
    dominant,     // "S2"
    secondary,    // "S1"
    flexibility,  // 0–10
    adaptability, // 0–10
    total: answered,

    // App.js compatibility — onSurveyComplete reads result.scales
    scales: scores,

    // App.js meta fields
    primary:   s0,
    tertiary:  s2,
    least:     s3,

    // Perf/pot fields (not applicable to SL — use flexibility as proxy)
    perfAvg:  flexibility,
    potAvg:   adaptability,
    perfBox:  null,
    potBox:   null,
  };
}

export default scoreSL;
